import type {
  CreateCategory,
  CreateRecordReferenceType,
  ProductConfiguration,
  RecordReferenceRule,
  UpdateCategory,
  UpdateProductConfiguration,
} from '@easyhelp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiRequest } from './client';
import { productsQueryKey } from './consumers';

/** Chiave della configurazione di un prodotto (categorie, lunghezza minima, tipi di riferimento, regole). */
export const productConfigurationQueryKey = (productId: string) => ['products', productId, 'configuration'] as const;

/** `GET /products/:id/configuration`: la configurazione letta dai consumer del prodotto (#18). */
export function useProductConfiguration(productId: string) {
  return useQuery({
    queryKey: productConfigurationQueryKey(productId),
    queryFn: () => apiRequest<ProductConfiguration>(`/products/${productId}/configuration`),
  });
}

/**
 * Ogni modifica restituisce la configurazione aggiornata: la mettiamo in cache e invalidiamo
 * l'elenco dei prodotti (il blocco del prefisso e `updatedAt` li decide l'API).
 */
function useStoreConfiguration() {
  const queryClient = useQueryClient();
  return (configuration: ProductConfiguration) => {
    queryClient.setQueryData(productConfigurationQueryKey(configuration.product.id), configuration);
    return queryClient.invalidateQueries({ queryKey: productsQueryKey, exact: true });
  };
}

/** `PATCH /products/:id/configuration`: lunghezza minima della descrizione. */
export function useUpdateProductConfiguration() {
  const store = useStoreConfiguration();
  return useMutation<ProductConfiguration, ApiError, { productId: string; changes: UpdateProductConfiguration }>({
    mutationFn: ({ productId, changes }) =>
      apiRequest<ProductConfiguration>(`/products/${productId}/configuration`, {
        method: 'PATCH',
        body: JSON.stringify(changes),
      }),
    onSuccess: store,
  });
}

/** `POST /products/:id/categories`: nuova voce in coda al set del canale (409 se già presente). */
export function useAddCategory() {
  const store = useStoreConfiguration();
  return useMutation<ProductConfiguration, ApiError, { productId: string; input: CreateCategory }>({
    mutationFn: ({ productId, input }) =>
      apiRequest<ProductConfiguration>(`/products/${productId}/categories`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: store,
  });
}

/** `PATCH /products/:id/categories/:categoryId`: rinomina e/o sposta una voce nel suo set. */
export function useUpdateCategory() {
  const store = useStoreConfiguration();
  return useMutation<
    ProductConfiguration,
    ApiError,
    { productId: string; categoryId: string; changes: UpdateCategory }
  >({
    mutationFn: ({ productId, categoryId, changes }) =>
      apiRequest<ProductConfiguration>(`/products/${productId}/categories/${categoryId}`, {
        method: 'PATCH',
        body: JSON.stringify(changes),
      }),
    onSuccess: store,
  });
}

/** `DELETE /products/:id/categories/:categoryId`: rimozione logica; i ticket già aperti non cambiano. */
export function useRemoveCategory() {
  const store = useStoreConfiguration();
  return useMutation<ProductConfiguration, ApiError, { productId: string; categoryId: string }>({
    mutationFn: ({ productId, categoryId }) =>
      apiRequest<ProductConfiguration>(`/products/${productId}/categories/${categoryId}`, { method: 'DELETE' }),
    onSuccess: store,
  });
}

/** `POST /products/:id/record-reference-types`: nuovo tipo di riferimento al record (409 se la chiave c'è già). */
export function useAddRecordReferenceType() {
  const store = useStoreConfiguration();
  return useMutation<ProductConfiguration, ApiError, { productId: string; input: CreateRecordReferenceType }>({
    mutationFn: ({ productId, input }) =>
      apiRequest<ProductConfiguration>(`/products/${productId}/record-reference-types`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: store,
  });
}

/** `PATCH /products/:id/record-reference-types/:key`: nuova etichetta; la chiave resta. */
export function useUpdateRecordReferenceType() {
  const store = useStoreConfiguration();
  return useMutation<ProductConfiguration, ApiError, { productId: string; key: string; label: string }>({
    mutationFn: ({ productId, key, label }) =>
      apiRequest<ProductConfiguration>(`/products/${productId}/record-reference-types/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        body: JSON.stringify({ label }),
      }),
    onSuccess: store,
  });
}

/** `DELETE /products/:id/record-reference-types/:key`: i ticket già ricevuti conservano il riferimento. */
export function useRemoveRecordReferenceType() {
  const store = useStoreConfiguration();
  return useMutation<ProductConfiguration, ApiError, { productId: string; key: string }>({
    mutationFn: ({ productId, key }) =>
      apiRequest<ProductConfiguration>(`/products/${productId}/record-reference-types/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      }),
    onSuccess: store,
  });
}

/**
 * `PUT /products/:id/committenti/:committente/record-reference-rule`: regola «riferimento al record
 * consentito» (DEC-014), che vale per identificativo paziente e riferimento al record.
 */
export function useSetRecordReferenceRule() {
  const store = useStoreConfiguration();
  return useMutation<
    ProductConfiguration,
    ApiError,
    { productId: string; committente: string; rule: RecordReferenceRule }
  >({
    mutationFn: ({ productId, committente, rule }) =>
      apiRequest<ProductConfiguration>(
        `/products/${productId}/committenti/${encodeURIComponent(committente)}/record-reference-rule`,
        { method: 'PUT', body: JSON.stringify(rule) }
      ),
    onSuccess: store,
  });
}
